import { Vector2, Vector3 } from "three";
import { octree, type Octree } from "d3-octree";
import {
  cylinderDistance,
  cylinderNormal,
  type CylinderSurface,
} from "./cylinder-surface";
import {
  cylinderAxisFromNormals,
  refineCylinderGeometric,
} from "./cylinder-fit";
import { fitCircleRobust } from "./circle-fit";
import { PipeFitNeighborhood } from "./pipe-fit-neighborhood";

export type PipeSample = {
  point: Vector3;
  normal: Vector3;
  spacing: number;
  supportSpacing?: number;
};
export type PipeFrontier = { center: Vector3; axis: Vector3; radius: number };
export type PipeStep = {
  frontier: PipeFrontier;
  cylinder: CylinderSurface;
  samples: PipeSample[];
  tolerance: number;
};

const MIN_SUPPORT = 24;
const MAX_FIT_SAMPLES = 1024;
const MIN_AXIS_AGREEMENT = Math.cos(Math.PI / 4);
const MIN_NORMAL_AGREEMENT = Math.cos(Math.PI / 6);
const LIMITS = {
  minimumAxisLengthSquared: 0.9,
  inlierRatio: 0.8,
  minimumResidual: 0.0005,
  residualRadiusRatio: 0.12,
  toleranceRadiusRatio: 0.2,
  minimumStep: 0.005,
  stepRadiusRatio: 1,
  halfStepOverlap: 1.5,
  corridorRadiusRatio: 1.35,
  normalAxisLimit: 0.7,
  minimumRadiusRatio: 0.75,
  maximumRadiusRatio: 1.25,
  centerOffsetRatio: 0.45,
  minimumTransitionRadius: 0.08,
  inletToleranceRatio: 0.15,
  neighborRadiusRatio: 0.75,
  minimumNeighborRadiusRatio: 0.15,
  neighborSpacingRatio: 2.5,
  neighborChecks: 2_000_000,
  outletProbes: 192,
  outletFitRadiusRatio: 2.2,
  boundaryRatio: 0.95,
};

/**
 * @param points Bounded copied scan evidence, not imported normal attributes.
 * @param spacing Nominal source spacing in metres.
 * @param normals Optional job-scoped cache over immutable point identities.
 * @param supportSpacing Retained-sample spacing for display support, independent of fitting noise.
 * @returns Lazy position-derived normals shared within one query neighborhood.
 */
export function pipeSamplesFromPoints(
  points: Vector3[],
  spacing: number,
  normals = new WeakMap<Vector3, Vector3 | undefined>(),
  supportSpacing = spacing,
): PipeSample[] {
  const neighborhood = new PipeFitNeighborhood(points);
  return points.map((point) => ({
    point,
    spacing,
    supportSpacing,
    get normal() {
      const cached = normals.get(point);
      if (cached) return cached.clone();
      const estimated = neighborhood.normal(point);
      if (estimated) normals.set(point, estimated);
      return estimated?.clone() ?? new Vector3();
    },
  }));
}

/**
 * @param samples Bounded samples to index, with positions in one local frame.
 * @returns The existing octree's spatial index.
 */
export function pipeSampleIndex(samples: PipeSample[]): Octree<PipeSample> {
  return octree(
    samples,
    ({ point }) => point.x,
    ({ point }) => point.y,
    ({ point }) => point.z,
  );
}

/**
 * @param tree Local sample index.
 * @param point Query position.
 * @param radius Query radius in metres.
 * @param limit Maximum returned neighbors.
 * @returns Nearby samples, ordered by distance.
 */
export function pipeNeighbors(
  tree: Octree<PipeSample>,
  point: Vector3,
  radius: number,
  limit = 128,
): PipeSample[] {
  const neighbors: Array<{ sample: PipeSample; distance: number }> = [];
  tree.visit((node, x0, y0, z0, x1, y1, z1) => {
    if (
      x0 > point.x + radius ||
      x1 < point.x - radius ||
      y0 > point.y + radius ||
      y1 < point.y - radius ||
      z0 > point.z + radius ||
      z1 < point.z - radius
    ) {
      return true;
    }
    if (Array.isArray(node)) return false;
    let leaf: typeof node | undefined = node;
    while (leaf) {
      const distance = leaf.data.point.distanceToSquared(point);
      if (distance <= radius * radius) {
        neighbors.push({ sample: leaf.data, distance });
      }
      leaf = leaf.next && !Array.isArray(leaf.next) ? leaf.next : undefined;
    }
    return false;
  });
  return neighbors
    .sort((left, right) => left.distance - right.distance)
    .slice(0, limit)
    .map(({ sample }) => sample);
}

/**
 * @param samples Local observed points with position-derived normals.
 * @param cancelled Cancellation check for the current tracing job.
 * @param radiusRange Optional broad bounds for one local continuation, not the initial run radius.
 * @returns A conditioned, supported tube section without a requested direction.
 */
export async function fitPipeSection(
  samples: PipeSample[],
  cancelled: () => boolean = () => false,
  radiusRange?: readonly [number, number],
): Promise<Omit<PipeStep, "frontier"> | undefined> {
  if (samples.length < MIN_SUPPORT) return;
  const stride = Math.max(1, Math.ceil(samples.length / MAX_FIT_SAMPLES));
  const local = samples.filter((_, index) => index % stride === 0);
  const axis = cylinderAxisFromNormals(local.map(({ normal }) => normal));
  if (!axis || cancelled()) return;
  const radial = new Vector3(1, 0, 0).projectOnPlane(axis);
  if (radial.lengthSq() < 0.1) radial.set(0, 0, 1).projectOnPlane(axis);
  radial.normalize();
  const tangent = new Vector3().crossVectors(axis, radial);
  const center = local
    .reduce((sum, { point }) => sum.add(point), new Vector3())
    .divideScalar(local.length);
  const spacing = Math.min(...local.map((sample) => sample.spacing));
  const circle = fitCircleRobust(
    local.map(({ point }) => {
      const offset = point.clone().sub(center);
      return new Vector2(offset.dot(radial), offset.dot(tangent));
    }),
    spacing,
    LIMITS.minimumResidual,
  );
  if (!circle) return;
  if (
    radiusRange &&
    (circle.radius < radiusRange[0] || circle.radius > radiusRange[1])
  ) {
    return;
  }
  const fitted = await refineCylinderGeometric(
    local.map(({ point }) => point),
    {
      axis,
      radial,
      radius: circle.radius,
      center: center
        .addScaledVector(radial, circle.center.x)
        .addScaledVector(tangent, circle.center.y),
    },
    { spacing, cancelled },
  );
  if (!fitted.accepted || cancelled()) return;
  const { cylinder } = fitted;
  const candidate = cylinder;
  const residuals = local
    .map(({ point }) => Math.abs(cylinderDistance(candidate, point)))
    .sort((left, right) => left - right);
  const residual = residuals[Math.floor(residuals.length * LIMITS.inlierRatio)];
  if (
    residual >
    Math.max(
      LIMITS.minimumResidual,
      cylinder.radius * LIMITS.residualRadiusRatio,
    )
  ) {
    return;
  }
  const tolerance = Math.min(
    cylinder.radius * LIMITS.toleranceRadiusRatio,
    Math.max(LIMITS.minimumResidual, residual * 3),
  );
  const inliers = local.filter(
    ({ point, normal }) =>
      Math.abs(cylinderDistance(candidate, point)) <= tolerance &&
      Math.abs(normal.dot(cylinderNormal(candidate, point))) >=
        MIN_NORMAL_AGREEMENT,
  );
  if (
    inliers.length < MIN_SUPPORT ||
    inliers.length < local.length * LIMITS.inlierRatio
  ) {
    return;
  }
  return { cylinder, samples: inliers, tolerance };
}

/**
 * @param radius Last supported radius in metres.
 * @returns Overlapping local tracing step, independent of total run length.
 */
export function pipeStepLength(radius: number): number {
  return Math.max(LIMITS.minimumStep, radius * LIMITS.stepRadiusRatio);
}

/**
 * @param samples Bounded observed surface samples around the next step.
 * @param frontier Last accepted centre, tangent and radius.
 * @param cancelled Cancellation check for the current tracing job.
 * @returns A supported local tube section; undefined leaves the frontier unchanged.
 */
export async function fitPipeStep(
  samples: PipeSample[],
  frontier: PipeFrontier,
  cancelled: () => boolean = () => false,
): Promise<PipeStep | undefined> {
  const step = pipeStepLength(frontier.radius);
  const predicted = frontier.center
    .clone()
    .addScaledVector(frontier.axis, step);
  const offset = new Vector3();
  const nearby = samples.filter(({ point }) => {
    offset.subVectors(point, predicted);
    const axial = offset.dot(frontier.axis);
    return (
      Math.abs(axial) <= step * LIMITS.halfStepOverlap &&
      offset.lengthSq() - axial * axial <=
        (frontier.radius * LIMITS.corridorRadiusRatio) ** 2
    );
  });
  const stride = Math.max(1, Math.ceil(nearby.length / MAX_FIT_SAMPLES));
  const local = nearby.filter(
    (sample, index) =>
      index % stride === 0 &&
      Math.abs(sample.normal.dot(frontier.axis)) < LIMITS.normalAxisLimit,
  );
  if (local.length < MIN_SUPPORT) return;
  const fitted = await fitPipeSection(local, cancelled);
  if (!fitted) return;
  const { cylinder, tolerance } = fitted;
  if (
    Math.abs(cylinder.axis.dot(frontier.axis)) < MIN_AXIS_AGREEMENT ||
    cylinder.radius < frontier.radius * LIMITS.minimumRadiusRatio ||
    cylinder.radius > frontier.radius * LIMITS.maximumRadiusRatio
  ) {
    return;
  }
  if (cylinder.axis.dot(frontier.axis) < 0) cylinder.axis.negate();
  cylinder.center.addScaledVector(
    cylinder.axis,
    predicted.clone().sub(cylinder.center).dot(cylinder.axis),
  );
  if (
    cylinder.center.distanceTo(predicted) >
    frontier.radius * LIMITS.centerOffsetRatio
  ) {
    return;
  }
  const candidate = cylinder;
  const selected = samples.filter((sample) => {
    const { point } = sample;
    const axial = offset
      .subVectors(point, candidate.center)
      .dot(candidate.axis);
    return (
      Math.abs(axial) <= step * LIMITS.halfStepOverlap &&
      Math.abs(cylinderDistance(candidate, point)) <= tolerance &&
      Math.abs(sample.normal.dot(cylinderNormal(candidate, point))) >=
        MIN_NORMAL_AGREEMENT
    );
  });
  if (
    !selected.some(
      ({ point }) =>
        offset.subVectors(point, candidate.center).dot(candidate.axis) >
        step / 4,
    )
  ) {
    return;
  }
  return {
    frontier: {
      center: cylinder.center.clone(),
      axis: cylinder.axis.clone(),
      radius: cylinder.radius,
    },
    cylinder,
    samples: selected,
    tolerance,
  };
}

export type PipeTransition = {
  kind: "outlet" | "junction" | "unresolved";
  outlets: PipeStep[];
  samples: PipeSample[];
};

/**
 * @param radius Supported inlet radius.
 * @returns Maximum size of one fitting search, not a total run extent.
 */
export function pipeTransitionRadius(radius: number): number {
  return Math.min(1, Math.max(LIMITS.minimumTransitionRadius, radius * 12));
}

/**
 * @param samples Bounded samples surrounding a suspected fitting.
 * @param frontier Supported inlet of the transition.
 * @param cancelled Cancellation check for the current tracing job.
 * @returns Connected fitting evidence with explicit outlet ambiguity.
 */
export async function fitPipeTransition(
  samples: PipeSample[],
  frontier: PipeFrontier,
  cancelled: () => boolean = () => false,
): Promise<PipeTransition> {
  const reach = pipeTransitionRadius(frontier.radius);
  const step = pipeStepLength(frontier.radius);
  const offset = new Vector3();
  const local = samples.filter(
    ({ point }) =>
      point.distanceTo(frontier.center) <= reach &&
      offset.subVectors(point, frontier.center).dot(frontier.axis) >= -step,
  );
  const tree = pipeSampleIndex(local);
  const radial = new Vector3(0, 0, 1).projectOnPlane(frontier.axis).normalize();
  const inlet = { ...frontier, radial };
  const queue = local.filter(
    ({ point }) =>
      Math.abs(offset.subVectors(point, frontier.center).dot(frontier.axis)) <=
        step / 2 &&
      Math.abs(cylinderDistance(inlet, point)) <=
        frontier.radius * LIMITS.inletToleranceRatio,
  );
  const reached = new Set(queue);
  let checks = 0;
  for (const sample of queue) {
    if (cancelled()) return { kind: "unresolved", outlets: [], samples: [] };
    const distance = Math.min(
      frontier.radius * LIMITS.neighborRadiusRatio,
      Math.max(
        frontier.radius * LIMITS.minimumNeighborRadiusRatio,
        sample.spacing * LIMITS.neighborSpacingRatio,
      ),
    );
    for (const neighbor of pipeNeighbors(tree, sample.point, distance)) {
      if (++checks > LIMITS.neighborChecks) {
        return { kind: "unresolved", outlets: [], samples: [] };
      }
      const mutual = Math.min(
        distance,
        Math.max(
          frontier.radius * LIMITS.minimumNeighborRadiusRatio,
          neighbor.spacing * LIMITS.neighborSpacingRatio,
        ),
      );
      if (
        reached.has(neighbor) ||
        sample.point.distanceToSquared(neighbor.point) > mutual * mutual
      ) {
        continue;
      }
      reached.add(neighbor);
      queue.push(neighbor);
    }
  }
  const connectedTree = pipeSampleIndex(queue);
  const outlets: PipeStep[] = [];
  const probes: Vector3[] = [];
  const stride = Math.max(1, Math.ceil(queue.length / LIMITS.outletProbes));
  for (let index = 0; index < queue.length; index += stride) {
    if (cancelled()) return { kind: "unresolved", outlets: [], samples: [] };
    const sample = queue[index];
    if (sample.point.distanceTo(frontier.center) < step * 2) continue;
    if (
      probes.some(
        (point) => point.distanceToSquared(sample.point) < frontier.radius ** 2,
      )
    ) {
      continue;
    }
    probes.push(sample.point);
    if (
      outlets.some(
        ({ cylinder, tolerance }) =>
          Math.abs(cylinderDistance(cylinder, sample.point)) <= tolerance &&
          Math.abs(sample.normal.dot(cylinderNormal(cylinder, sample.point))) >=
            MIN_NORMAL_AGREEMENT,
      )
    ) {
      continue;
    }
    const neighbors = pipeNeighbors(
      connectedTree,
      sample.point,
      frontier.radius * LIMITS.outletFitRadiusRatio,
      512,
    );
    const section = await fitPipeSection(neighbors, cancelled, [
      frontier.radius * 0.5,
      frontier.radius * 2,
    ]);
    if (!section) continue;
    const { cylinder } = section;
    if (
      cylinder.radius < frontier.radius * 0.5 ||
      cylinder.radius > frontier.radius * 2
    ) {
      continue;
    }
    offset.subVectors(cylinder.center, frontier.center);
    if (offset.dot(frontier.axis) < step || offset.length() < step * 2) {
      continue;
    }
    if (cylinder.axis.dot(offset) < 0) cylinder.axis.negate();
    if (cylinder.axis.dot(offset.clone().normalize()) < 0.5) continue;
    const duplicate = outlets.some(
      ({ cylinder: previous }) =>
        previous.axis.dot(cylinder.axis) > MIN_NORMAL_AGREEMENT &&
        offset
          .subVectors(cylinder.center, previous.center)
          .cross(previous.axis)
          .length() < frontier.radius,
    );
    if (!duplicate) {
      outlets.push({
        ...section,
        frontier: {
          center: cylinder.center.clone(),
          axis: cylinder.axis.clone(),
          radius: cylinder.radius,
        },
      });
    }
  }
  if (!outlets.length) return { kind: "unresolved", outlets, samples: [] };
  const unbounded = queue.some(
    ({ point }) =>
      point.distanceTo(frontier.center) > reach * LIMITS.boundaryRatio &&
      outlets.every(
        ({ cylinder, tolerance }) =>
          Math.abs(cylinderDistance(cylinder, point)) > tolerance * 2,
      ),
  );
  if (unbounded) return { kind: "unresolved", outlets: [], samples: [] };
  if (outlets.length > 1) return { kind: "junction", outlets, samples: [] };
  const outlet = outlets[0];
  return {
    kind: "outlet",
    outlets,
    samples: queue.filter(
      ({ point }) =>
        offset
          .subVectors(point, outlet.frontier.center)
          .dot(outlet.frontier.axis) <=
        step * 0.5,
    ),
  };
}
